import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { BackendService } from '../../services/general/backend.service';

@Component({
    selector: 'app-nerf-simulator',
    templateUrl: './nerf-simulator.component.html',
    styleUrls: ['./nerf-simulator.component.scss'],
    standalone: false
})
export class NerfSimulatorComponent implements OnInit {
  @Input() projectId: any;

  public simulatorForm: FormGroup;
  public loading = false;
  public simulationResult: any = null;
  public hasResult = false;
  public simulationHistory: any[] = [];
  public loadingHistory = false;
  public savingSimulation = false;
  public currentSimulationId: number | null = null;
  public isSimulationSaved = false;

  /** Colonne tabella storico (mat-table + table-primary come nel resto dell'app) */
  readonly historyDisplayedColumns: string[] = [
    'created_at',
    'utilization_percent',
    'patients_per_month',
    'actions',
  ];

  constructor(
    private fb: FormBuilder,
    private backend: BackendService
  ) {
    this.simulatorForm = this.fb.group({
      // Medication parameters (compositions stored as percentages for display)
      rituximab_min: [180, [Validators.required, Validators.min(0)]],
      rituximab_composition: [40, [Validators.required, Validators.min(0), Validators.max(100)]],
      ocrelizumab_min: [180, [Validators.required, Validators.min(0)]],
      ocrelizumab_composition: [40, [Validators.required, Validators.min(0), Validators.max(100)]],
      ublituximab_min: [60, [Validators.required, Validators.min(0)]],
      ublituximab_composition: [20, [Validators.required, Validators.min(0), Validators.max(100)]],

      // Working hours parameters
      Hours_Per_Day: [8, [Validators.required, Validators.min(0)]],
      Gap_Min: [15, [Validators.required, Validators.min(0)]],
      Working_Days_Month: [20, [Validators.required, Validators.min(0)]],
      Avg_Infusion_Min_Scenario: [156, [Validators.required, Validators.min(0)]],
      Safety_Buffer: [10, [Validators.required, Validators.min(0), Validators.max(100)]],
      Gap_TimeHours_Per_Day: [2, [Validators.required, Validators.min(0)]],

      // Staff parameters
      Nurses_per_Shift: [3, [Validators.required, Validators.min(0)]],
      Patients_per_Nurse: [2, [Validators.required, Validators.min(0)]],

      // Capacity parameters
      avg_patients_per_chair_per_day: [2.470149, [Validators.required, Validators.min(0)]],
      Chairs_Active: [5, [Validators.required, Validators.min(0)]],
      number_of_opening_days: [20, [Validators.required, Validators.min(0)]],

      // Time parameters
      prep_time_chair_materials: [20, [Validators.required, Validators.min(0)]],
      patient_reception_assessment_time: [26, [Validators.required, Validators.min(0)]],
      vitals_premedication_setup_time: [28, [Validators.required, Validators.min(0)]],
      post_infusion_surveillance_time: [52, [Validators.required, Validators.min(0)]],
      post_infusion_admin_time: [17, [Validators.required, Validators.min(0)]],
      avg_operational_gap_between_infusions: [19, [Validators.required, Validators.min(0)]],
      infusion_service_hours: [8, [Validators.required, Validators.min(0)]]
    }, { validators: this.compositionSumValidator });
  }

  // Custom validator to ensure the sum of compositions is <= 100%
  private compositionSumValidator = (control: AbstractControl): ValidationErrors | null => {
    const rituximab = Number(control.get('rituximab_composition')?.value) || 0;
    const ocrelizumab = Number(control.get('ocrelizumab_composition')?.value) || 0;
    const ublituximab = Number(control.get('ublituximab_composition')?.value) || 0;

    const sum = rituximab + ocrelizumab + ublituximab;

    console.log('Composition Validator - Rituximab:', rituximab, 'Ocrelizumab:', ocrelizumab, 'Ublituximab:', ublituximab, 'Sum:', sum);

    if (sum > 100) {
      console.log('Validation FAILED - Sum exceeds 100%');
      return { compositionSumExceeded: { sum, max: 100 } };
    }

    console.log('Validation PASSED');
    return null;
  }

  ngOnInit(): void {
    // Disable read-only fields
    this.simulatorForm.get('rituximab_min')?.disable();
    this.simulatorForm.get('ocrelizumab_min')?.disable();
    this.simulatorForm.get('ublituximab_min')?.disable();
    this.simulatorForm.get('Hours_Per_Day')?.disable();
    this.simulatorForm.get('Working_Days_Month')?.disable();
    this.simulatorForm.get('avg_patients_per_chair_per_day')?.disable();
    this.simulatorForm.get('prep_time_chair_materials')?.disable();
    this.simulatorForm.get('patient_reception_assessment_time')?.disable();
    this.simulatorForm.get('vitals_premedication_setup_time')?.disable();
    this.simulatorForm.get('post_infusion_surveillance_time')?.disable();
    this.simulatorForm.get('post_infusion_admin_time')?.disable();
    this.simulatorForm.get('avg_operational_gap_between_infusions')?.disable();

    // Subscribe to composition field changes to trigger validation
    this.simulatorForm.get('rituximab_composition')?.valueChanges.subscribe(() => {
      this.simulatorForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });
    });
    this.simulatorForm.get('ocrelizumab_composition')?.valueChanges.subscribe(() => {
      this.simulatorForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });
    });
    this.simulatorForm.get('ublituximab_composition')?.valueChanges.subscribe(() => {
      this.simulatorForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });
    });

    // Load simulation history
    this.loadSimulationHistory();
  }

  onSubmit(): void {
    if (this.simulatorForm.valid) {
      this.loading = true;
      this.hasResult = false;

      // Use getRawValue() to include disabled fields
      const formData = this.simulatorForm.getRawValue();

      // Convert percentage values to decimals for backend
      formData.rituximab_composition = formData.rituximab_composition / 100;
      formData.ocrelizumab_composition = formData.ocrelizumab_composition / 100;
      formData.ublituximab_composition = formData.ublituximab_composition / 100;
      formData.Safety_Buffer = formData.Safety_Buffer / 100;

      this.backend.post(`nerf-simulator/${this.projectId}`, formData).subscribe({
        next: (result: any) => {
          console.log('NERF Simulator Response:', result);
          console.log('Response type:', typeof result);

          let parsedResult = result;

          // Check if response has Lambda format (statusCode and body)
          if (result && result.statusCode && result.body) {
            console.log('Lambda response detected, extracting body...');
            try {
              // Parse the body string
              parsedResult = JSON.parse(result.body);
              console.log('Parsed body:', parsedResult);
            } catch (e) {
              console.error('Error parsing Lambda body:', e);
              this.backend.showErrors({ message: 'Invalid JSON in Lambda response body' });
              this.loading = false;
              return;
            }
          } else if (typeof result === 'string') {
            // Handle plain string response
            console.log('Response is a string, parsing JSON...');
            try {
              parsedResult = JSON.parse(result);
              console.log('Parsed result:', parsedResult);
            } catch (e) {
              console.error('Error parsing JSON:', e);
              this.backend.showErrors({ message: 'Invalid JSON response from server' });
              this.loading = false;
              return;
            }
          }

          console.log('Final result keys:', Object.keys(parsedResult));
          console.log('Final result:', parsedResult);
          this.simulationResult = parsedResult;
          this.hasResult = true;
          this.loading = false;
          this.isSimulationSaved = false;
          this.currentSimulationId = null; // Reset ID for new simulation

          // Automatically save the simulation to database
          console.log('Auto-save triggered after simulation');
          this.saveSimulation();
        },
        error: (error: any) => {
          console.error('Error running NERF simulation:', error);
          this.backend.showErrors(error);
          this.loading = false;
        }
      });
    }
  }

  onReset(): void {
    this.simulatorForm.reset({
      rituximab_min: 180,
      rituximab_composition: 40,
      ocrelizumab_min: 180,
      ocrelizumab_composition: 40,
      ublituximab_min: 60,
      ublituximab_composition: 20,
      Hours_Per_Day: 8,
      Gap_Min: 15,
      Working_Days_Month: 20,
      Avg_Infusion_Min_Scenario: 156,
      Safety_Buffer: 10,
      Gap_TimeHours_Per_Day: 2,
      Nurses_per_Shift: 3,
      Patients_per_Nurse: 2,
      avg_patients_per_chair_per_day: 2.470149,
      Chairs_Active: 5,
      number_of_opening_days: 20,
      prep_time_chair_materials: 20.104478,
      patient_reception_assessment_time: 26.298507,
      vitals_premedication_setup_time: 27.850746,
      post_infusion_surveillance_time: 52.388060,
      post_infusion_admin_time: 17.477612,
      avg_operational_gap_between_infusions: 19.164179,
      infusion_service_hours: 8
    });
    this.hasResult = false;
    this.simulationResult = null;
    this.currentSimulationId = null;
    this.isSimulationSaved = false;
  }

  saveSimulation(): void {
    if (!this.simulationResult) {
      this.backend.showErrors({ message: 'No simulation result to save' });
      return;
    }

    // Don't save if already saved
    if (this.isSimulationSaved) {
      console.log('Simulation already saved, skipping save operation');
      return;
    }

    this.savingSimulation = true;

    // Convert percentage values to decimals for backend before saving
    const formData = this.simulatorForm.getRawValue();
    const inputParams = {
      ...formData,
      rituximab_composition: formData.rituximab_composition / 100,
      ocrelizumab_composition: formData.ocrelizumab_composition / 100,
      ublituximab_composition: formData.ublituximab_composition / 100,
      Safety_Buffer: formData.Safety_Buffer / 100
    };

    const simulationData = {
      project_id: this.projectId,
      input_parameters: inputParams,
      output_results: this.simulationResult
    };

    this.backend.post('nerf-simulations/save', simulationData).subscribe({
      next: (response: any) => {
        console.log('Simulation saved successfully:', response);
        this.currentSimulationId = response.simulation_id || response.id;
        this.isSimulationSaved = true;
        this.savingSimulation = false;
        this.loadSimulationHistory();
      },
      error: (error: any) => {
        console.error('Error saving simulation:', error);
        this.backend.showErrors(error);
        this.savingSimulation = false;
        this.isSimulationSaved = false;
      }
    });
  }

  loadSimulationHistory(): void {
    if (!this.projectId) {
      return;
    }

    this.loadingHistory = true;

    this.backend.get(`nerf-simulations/list/${this.projectId}`).subscribe({
      next: (response: any) => {
        console.log('Simulation history:', response);
        this.simulationHistory = response.simulations || response.data || response;
        this.loadingHistory = false;
      },
      error: (error: any) => {
        console.error('Error loading simulation history:', error);
        this.simulationHistory = [];
        this.loadingHistory = false;
      }
    });
  }

  loadSimulation(simulation: any): void {
    if (!simulation || !simulation.input_parameters) {
      return;
    }

    // Convert decimal values from backend to percentages for display
    const params = { ...simulation.input_parameters };
    params.rituximab_composition = (params.rituximab_composition || 0) * 100;
    params.ocrelizumab_composition = (params.ocrelizumab_composition || 0) * 100;
    params.ublituximab_composition = (params.ublituximab_composition || 0) * 100;
    params.Safety_Buffer = (params.Safety_Buffer || 0) * 100;

    // Load input parameters into form
    this.simulatorForm.patchValue(params);

    // Load results
    this.simulationResult = simulation.output_results;
    this.hasResult = true;
    this.currentSimulationId = simulation.id;
    this.isSimulationSaved = true; // This simulation is already saved

    // Scroll to results
    setTimeout(() => {
      const resultsElement = document.querySelector('.results-main-card');
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }

  exportToExcel(simulationId?: number): void {
    const idToExport = simulationId || this.currentSimulationId;

    if (!idToExport) {
      this.backend.showErrors({ message: 'No simulation to export' });
      return;
    }

    console.log('Starting Excel export for simulation ID:', idToExport);

    // Use getBlob method from BackendService
    this.backend.getBlob(`nerf-simulations/export/${idToExport}`).subscribe({
      next: (response: any) => {
        console.log('Excel export response received:', response);

        // Extract the blob from the response
        const blob = response.body || response;

        // Get filename from Content-Disposition header or use default
        let filename = `nerf-simulation-${idToExport}.xlsx`;
        const contentDisposition = response.headers?.get('Content-Disposition');
        if (contentDisposition) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }

        // Create a download link and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        console.log('Excel file downloaded successfully');
      },
      error: (error: any) => {
        console.error('Error downloading Excel file from backend:', error);
        console.error('Error status:', error?.status);
        console.error('Error message:', error?.message);

        // Fallback to CSV export if backend fails
        console.log('Attempting CSV export as fallback...');
        this.exportToCSV(idToExport);
      }
    });
  }

  exportToCSV(simulationId?: number): void {
    // Find the simulation data
    let simulation: any;

    if (simulationId) {
      simulation = this.simulationHistory.find(s => s.id === simulationId);
    } else if (this.currentSimulationId) {
      simulation = this.simulationHistory.find(s => s.id === this.currentSimulationId);
    }

    // If not found in history, use current result
    if (!simulation && this.simulationResult) {
      simulation = {
        id: this.currentSimulationId || 'current',
        input_parameters: this.simulatorForm.getRawValue(),
        output_results: this.simulationResult,
        created_at: new Date().toISOString()
      };
    }

    if (!simulation) {
      this.backend.showErrors({ message: 'No simulation data to export' });
      return;
    }

    // Create CSV content
    let csvContent = 'NERF Simulator - Simulation Results\n\n';
    csvContent += `Simulation ID:,${simulation.id}\n`;
    csvContent += `Date:,${new Date(simulation.created_at).toLocaleString()}\n\n`;

    // Input Parameters
    csvContent += 'INPUT PARAMETERS\n';
    csvContent += 'Parameter Name,Value,Unit\n';

    const inputParams = simulation.input_parameters;
    const paramUnits: any = {
      'rituximab_min': 'min',
      'rituximab_composition': '%',
      'ocrelizumab_min': 'min',
      'ocrelizumab_composition': '%',
      'ublituximab_min': 'min',
      'ublituximab_composition': '%',
      'Hours_Per_Day': 'h',
      'Gap_Min': 'min',
      'Working_Days_Month': 'days',
      'Avg_Infusion_Min_Scenario': 'min',
      'Safety_Buffer': '%',
      'Gap_TimeHours_Per_Day': 'h',
      'Nurses_per_Shift': '',
      'Patients_per_Nurse': '',
      'avg_patients_per_chair_per_day': '',
      'Chairs_Active': '',
      'number_of_opening_days': 'days',
      'prep_time_chair_materials': 'min',
      'patient_reception_assessment_time': 'min',
      'vitals_premedication_setup_time': 'min',
      'post_infusion_surveillance_time': 'min',
      'post_infusion_admin_time': 'min',
      'avg_operational_gap_between_infusions': 'min',
      'infusion_service_hours': 'h'
    };

    for (const key in inputParams) {
      const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      csvContent += `${label},${inputParams[key]},${paramUnits[key] || ''}\n`;
    }

    // Output Results
    csvContent += '\nOUTPUT RESULTS\n';
    csvContent += 'Result Name,Value,Unit\n';

    const outputResults = simulation.output_results;
    const resultUnits: any = {
      'utilization_percent': '%',
      'patients_per_month': 'patients',
      'cycles_per_chair_day': 'cycles',
      'rotation_index': '',
      'effective_chairs': 'chairs',
      'nurse_capacity': '',
      'avg_infusion_time_mix': 'min'
    };

    for (const key in outputResults) {
      const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      const value = typeof outputResults[key] === 'number' ? outputResults[key].toFixed(4) : outputResults[key];
      csvContent += `${label},${value},${resultUnits[key] || ''}\n`;
    }

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `nerf-simulation-${simulation.id}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log('CSV export completed successfully');
  }

  handleDeleteClick(event: Event, simulationId: number): void {
    // Prevent all event propagation
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    console.log('=== DELETE BUTTON CLICKED ===');
    console.log('Event prevented and stopped');

    // Call the actual delete function
    this.deleteSimulation(simulationId);
  }

  deleteSimulation(simulationId: number): void {
    if (!confirm('Are you sure you want to delete this simulation?')) {
      console.log('Delete cancelled by user');
      return;
    }

    console.log('=== DELETE OPERATION START ===');
    console.log('Attempting to delete simulation ID:', simulationId);
    console.log('Delete endpoint URL:', `nerf-simulations/${simulationId}`);
    console.log('Using backend.destroy() method which sends HTTP DELETE');

    // Use destroy() method which actually does HTTP DELETE
    this.backend.destroy(`nerf-simulations/${simulationId}`).subscribe({
      next: (response: any) => {
        console.log('=== DELETE SUCCESS ===');
        console.log('Simulation deleted successfully:', response);

        // Remove from local history array immediately for better UX
        this.simulationHistory = this.simulationHistory.filter(s => s.id !== simulationId);
        console.log('Removed from local array. New history count:', this.simulationHistory.length);

        // Clear current simulation if it was deleted
        if (this.currentSimulationId === simulationId) {
          console.log('Clearing currently displayed simulation');
          this.currentSimulationId = null;
          this.hasResult = false;
          this.simulationResult = null;
          this.isSimulationSaved = false;
        }

        // Reload history from server to sync
        console.log('Reloading simulation history from server...');
        this.loadSimulationHistory();
      },
      error: (error: any) => {
        console.error('Error deleting simulation:', error);
        console.error('Error status:', error?.status);
        console.error('Error message:', error?.message);
        console.error('Full error object:', error);

        // Check if it's a 404 or backend not implemented
        if (error?.status === 404) {
          alert('Delete endpoint not found. The backend endpoint may not be implemented yet.\n\nEndpoint: DELETE /api/nerf-simulations/' + simulationId);
        } else if (error?.status === 0) {
          alert('Cannot connect to backend. Please check if the Laravel server is running.');
        } else {
          this.backend.showErrors(error);
        }
      }
    });
  }

}
